import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Modal from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ShoppingBag,
  Search,
  Loader2,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Clock,
  Barcode,
} from "lucide-react";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";

const PosSalesModal = ({
  show,
  onHide,
  currencySymbol = "$",
  onLoadSaleToCart,
  mode = "pos",
}) => {
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);

  // Search, Status Filter & Pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [salesList, setSalesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectingSaleId, setSelectingSaleId] = useState(null);
  const searchInputRef = useRef(null);
  const handleBarcodeOrSearchSubmitRef = useRef(null);
  const [paginationMeta, setPaginationMeta] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  // Debounce search input by 400ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch sales
  const fetchSales = useCallback(async () => {
    if (!show) return;
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 15,
        perPage: 15,
        sort_by: "created_at",
        sort_order: "desc",
      };
      if (currentStore?.id) params.store_id = currentStore.id;
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter && statusFilter !== "all") {
        params.status = statusFilter;
      }

      const res = await apiClient.get(API_ENDPOINTS.SALES.SALES, {
        params,
        timeout: 30000,
      });
      let list = res.data?.data || [];

      // Client-side fallback filter if backend does not filter on status
      if (statusFilter && statusFilter !== "all") {
        const sf = statusFilter.toLowerCase();
        list = list.filter((s) => {
          const saleStatus = (s.status || "").toLowerCase();
          const payStatus = (s.payment_status || "").toLowerCase();
          if (sf === "completed") {
            return (
              saleStatus === "completed" ||
              payStatus === "paid" ||
              payStatus === "completed"
            );
          }
          if (sf === "paid") {
            return payStatus === "paid" || payStatus === "completed";
          }
          if (sf === "pending") {
            return (
              payStatus === "pending" ||
              payStatus === "unpaid" ||
              saleStatus === "pending" ||
              parseFloat(s.balance_due || 0) > 0
            );
          }
          if (sf === "partially_paid") {
            return (
              payStatus === "partially-paid" ||
              payStatus === "partially_paid" ||
              payStatus === "partial"
            );
          }
          if (sf === "cancelled") {
            return (
              saleStatus === "cancelled" ||
              payStatus === "cancelled" ||
              payStatus === "refunded"
            );
          }
          if (sf === "draft") {
            return saleStatus === "draft";
          }
          return saleStatus === sf || payStatus === sf;
        });
      }

      setSalesList(list);
      setPaginationMeta({
        current_page: res.data?.meta?.current_page || currentPage,
        last_page: res.data?.meta?.last_page || 1,
        per_page: res.data?.meta?.per_page || 15,
        total: res.data?.meta?.total || list.length,
      });
    } catch (err) {
      console.error("Fetch sales error:", err);
      toast.error("Failed to load sales list");
    } finally {
      setIsLoading(false);
    }
  }, [show, currentPage, currentStore?.id, debouncedSearch, statusFilter]);

  useEffect(() => {
    if (show) {
      fetchSales();
    }
  }, [show, fetchSales]);

  // Handle Select Sale
  const handleSelectSale = async (sale) => {
    if (selectingSaleId) return;
    setSelectingSaleId(sale.id);

    try {
      if (mode === "pos" && onLoadSaleToCart) {
        await onLoadSaleToCart(sale);
        onHide();
      } else {
        toast.info(
          `Loading Sale #${sale.invoice_number || sale.id} into POS...`,
        );
        onHide();
        navigate(`/pos?sale_id=${sale.id}`);
      }
    } catch (err) {
      console.error("Error selecting sale:", err);
      toast.error("Failed to select sale");
    } finally {
      setSelectingSaleId(null);
    }
  };

  // Barcode Scan / Immediate Search Submission
  const handleBarcodeOrSearchSubmit = async (rawCode) => {
    const code = (rawCode || "").trim();
    if (!code) return;
    const cleanCode = code.replace(/^#/, "").trim();
    if (!cleanCode) return;

    setIsLoading(true);
    try {
      const params = {
        page: 1,
        per_page: 5,
        search: cleanCode,
      };
      if (currentStore?.id) params.store_id = currentStore.id;

      const res = await apiClient.get(API_ENDPOINTS.SALES.SALES, { params });
      const list = res.data?.data || [];

      const lower = cleanCode.toLowerCase();
      let matchedSale = list.find((s) => {
        const inv = (s.invoice_number || "").toLowerCase().replace(/^#/, "");
        const cartNo = (s.cart_number || "").toLowerCase().replace(/^#/, "");
        return inv === lower || cartNo === lower || String(s.id) === cleanCode;
      });

      if (!matchedSale && list.length === 1) {
        matchedSale = list[0];
      }

      if (!matchedSale) {
        try {
          const detailRes = await apiClient.get(
            API_ENDPOINTS.SALES.SALE_DETAILS(cleanCode),
          );
          if (detailRes.data?.data) matchedSale = detailRes.data.data;
          else if (detailRes.data) matchedSale = detailRes.data;
        } catch {
          // not direct id
        }
      }

      // If record is available, load it into POS
      if (matchedSale) {
        await handleSelectSale(matchedSale);
        return;
      }

      // Unless no -> notify user that no record is available
      toast.error(`No sale found matching invoice barcode "${code}"`);
    } catch (err) {
      console.error("Barcode sale scan error:", err);
      toast.error(`Failed to lookup sale for barcode "${code}"`);
    } finally {
      setIsLoading(false);
    }
  };

  handleBarcodeOrSearchSubmitRef.current = handleBarcodeOrSearchSubmit;

  // Auto-focus search input when modal opens
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [show]);

  // Global barcode scanner listener when modal is open
  useEffect(() => {
    if (!show) return;
    let scanBuffer = "";
    let lastKeyTime = 0;

    const handleGlobalScan = (e) => {
      // If focused in the input, input's onKeyDown handles Enter
      if (document.activeElement === searchInputRef.current) {
        return;
      }

      const now = Date.now();
      if (e.key === "Enter" || e.keyCode === 13) {
        if (scanBuffer.length >= 2 && now - lastKeyTime < 250) {
          e.preventDefault();
          const code = scanBuffer.trim();
          scanBuffer = "";
          if (code) {
            handleBarcodeOrSearchSubmitRef.current?.(code);
          }
        } else {
          scanBuffer = "";
        }
        return;
      }

      if (e.key && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        if (now - lastKeyTime > 80) {
          scanBuffer = e.key;
        } else {
          scanBuffer += e.key;
        }
        lastKeyTime = now;
      }
    };

    window.addEventListener("keydown", handleGlobalScan);
    return () => window.removeEventListener("keydown", handleGlobalScan);
  }, [show]);

  // Helper for sale status badge
  const getSaleStatusBadge = (rawStatus) => {
    const s = (
      typeof rawStatus === "string"
        ? rawStatus
        : rawStatus?.value || rawStatus?.label || ""
    ).toLowerCase();
    const label =
      typeof rawStatus === "object"
        ? rawStatus?.label || rawStatus?.value || "Completed"
        : rawStatus || "Completed";

    if (s === "completed" || s === "paid" || s === "success") {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 font-semibold capitalize"
        >
          {label}
        </Badge>
      );
    }
    if (s === "cancelled" || s === "void") {
      return (
        <Badge
          variant="destructive"
          className="text-[10px] py-0 h-4 font-semibold capitalize"
        >
          {label || "Cancelled"}
        </Badge>
      );
    }
    if (s === "draft") {
      return (
        <Badge
          variant="secondary"
          className="text-[10px] py-0 h-4 font-semibold capitalize"
        >
          Draft
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="text-[10px] py-0 h-4 font-semibold capitalize"
      >
        {label || "Ordered"}
      </Badge>
    );
  };

  // Helper to reliably compute paid amount across various API representations
  const getSalePaidAmount = (sale) => {
    if (!sale) return 0;
    if (sale.paid_amount != null) {
      return parseFloat(sale.paid_amount || 0);
    }
    if (Array.isArray(sale.payments) && sale.payments.length > 0) {
      return sale.payments.reduce((acc, p) => {
        const pStatus = (p.payment_status || "").toLowerCase();
        if (pStatus !== "failed" && pStatus !== "cancelled") {
          return acc + parseFloat(p.amount || 0);
        }
        return acc;
      }, 0);
    }
    const payStatus = (sale.payment_status || "").toLowerCase();
    const saleStatus = (sale.status || "").toLowerCase();
    if (payStatus === "completed" || payStatus === "paid" || saleStatus === "completed") {
      return parseFloat(sale.total || 0);
    }
    return 0;
  };

  // Helper to reliably compute balance due
  const getSaleBalanceDue = (sale) => {
    if (!sale) return 0;
    if (sale.balance_due != null) {
      return parseFloat(sale.balance_due);
    }
    const payStatus = (sale.payment_status || "").toLowerCase();
    const saleStatus = (sale.status || "").toLowerCase();
    if (
      payStatus === "completed" ||
      payStatus === "paid" ||
      (saleStatus === "completed" && payStatus !== "pending" && payStatus !== "partially-paid")
    ) {
      return 0;
    }
    const total = parseFloat(sale.total || 0);
    const paid = getSalePaidAmount(sale);
    return Math.max(0, parseFloat((total - paid).toFixed(2)));
  };

  // Helper for payment status badge
  const getPaymentStatusBadge = (sale) => {
    if (!sale) return null;
    const rawStatus = (sale.payment_status || "").toLowerCase().replace(/[-_]/g, " ").trim();
    const total = parseFloat(sale.total || 0);
    const paid = getSalePaidAmount(sale);
    const balance = getSaleBalanceDue(sale);

    // 1. Fully Paid / Completed
    if (
      rawStatus === "completed" ||
      rawStatus === "paid" ||
      rawStatus === "success" ||
      (total > 0 && paid >= total - 0.01 && balance <= 0.01) ||
      (sale.status === "completed" && balance <= 0.01 && rawStatus !== "pending" && rawStatus !== "partially paid")
    ) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 bg-emerald-500/10 font-semibold"
        >
          Paid
        </Badge>
      );
    }

    // 2. Partially Paid
    if (
      rawStatus === "partially paid" ||
      rawStatus === "partial" ||
      (paid > 0.01 && balance > 0.01)
    ) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 font-semibold"
        >
          Partially Paid
        </Badge>
      );
    }

    // 3. Partially Refunded
    if (rawStatus === "partially refunded") {
      return (
        <Badge
          variant="outline"
          className="text-[10px] py-0 h-4 bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 font-semibold"
        >
          Partially Refunded
        </Badge>
      );
    }

    // 4. Refunded
    if (rawStatus === "refunded") {
      return (
        <Badge
          variant="secondary"
          className="text-[10px] py-0 h-4 font-semibold text-muted-foreground"
        >
          Refunded
        </Badge>
      );
    }

    // 5. Cancelled / Failed
    if (rawStatus === "cancelled" || rawStatus === "failed" || sale.status === "cancelled") {
      return (
        <Badge
          variant="destructive"
          className="text-[10px] py-0 h-4 font-semibold"
        >
          {rawStatus === "failed" ? "Failed" : "Cancelled"}
        </Badge>
      );
    }

    return (
      <Badge
        variant="outline"
        className="text-[10px] py-0 h-4 bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 font-semibold"
      >
        Pending Payment
      </Badge>
    );
  };

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && onHide()}
      title="Sales"
      icon={ShoppingBag}
      maxWidth="max-w-5xl"
      footer={
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 w-full">
          <div className="text-xs text-muted-foreground">
            Total sales: {paginationMeta.total}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={paginationMeta.current_page <= 1 || isLoading}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            <span className="text-xs text-muted-foreground font-mono">
              {paginationMeta.current_page} / {paginationMeta.last_page}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={
                paginationMeta.current_page >= paginationMeta.last_page ||
                isLoading
              }
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={onHide}>
              Close
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3.5 py-1">
        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-2.5 items-stretch md:items-center justify-between">
          <div className="relative flex-1">
            <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Scan barcode or search by Invoice # (e.g. INV-001), Customer, Phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.keyCode === 13) {
                  e.preventDefault();
                  handleBarcodeOrSearchSubmit(searchTerm);
                }
              }}
              className="pl-9 h-8 text-xs bg-muted/20"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground"
                onClick={() => setSearchTerm("")}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Select
              value={statusFilter}
              onValueChange={(val) => {
                setStatusFilter(val);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-8 text-xs w-40">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="partially_paid">Partially Paid</SelectItem>
                <SelectItem value="pending">Pending Payment</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={fetchSales}
              title="Refresh sales list"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>

        {/* Sales Table */}
        <div className="border rounded-xl overflow-hidden min-h-[320px] max-h-[500px] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-xs">Loading sales records...</p>
            </div>
          ) : salesList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2 text-center">
              <ShoppingBag className="h-10 w-10 text-muted-foreground/40" />
              <p className="text-sm font-medium text-foreground">
                No sales found
              </p>
              <p className="text-xs">
                {debouncedSearch
                  ? `No sales matching "${debouncedSearch}"`
                  : "No sales records found."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-xs bg-muted/30">
                  <TableHead className="font-semibold">Invoice #</TableHead>
                  <TableHead className="font-semibold">Customer</TableHead>
                  <TableHead className="font-semibold">Items</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Payment</TableHead>
                  <TableHead className="text-right font-semibold">
                    Total
                  </TableHead>
                  <TableHead className="text-right font-semibold w-24">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesList.map((sale) => {
                  const total = parseFloat(sale.total || 0);
                  const balance = getSaleBalanceDue(sale);

                  return (
                    <TableRow
                      key={sale.id}
                      className="text-xs hover:bg-muted/30"
                    >
                      {/* Invoice */}
                      <TableCell>
                        <div className="font-mono font-bold text-foreground">
                          {sale.invoice_number || `INV-${sale.id}`}
                        </div>
                        <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Clock className="h-3 w-3" />
                          {formatDate(sale.created_at || sale.date)}
                        </div>
                      </TableCell>

                      {/* Customer */}
                      <TableCell>
                        <div className="font-semibold text-foreground truncate max-w-[150px]">
                          {sale.customer?.full_name ||
                            sale.customer?.name ||
                            "Walk-in Customer"}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate max-w-[150px]">
                          {sale.customer?.phone ||
                            sale.customer?.email ||
                            "No contact info"}
                        </div>
                      </TableCell>

                      {/* Items */}
                      <TableCell>
                        <div className="font-medium text-foreground">
                          {sale.items?.length || 0} product
                          {(sale.items?.length || 0) !== 1 ? "s" : ""}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-[160px]">
                          {sale.items?.[0]?.product?.name ||
                            sale.items?.[0]?.product_data?.name ||
                            ""}
                          {(sale.items?.length || 0) > 1 ? " & more" : ""}
                        </div>
                      </TableCell>

                      {/* Sale Status */}
                      <TableCell>{getSaleStatusBadge(sale.status)}</TableCell>

                      {/* Payment Status */}
                      <TableCell>{getPaymentStatusBadge(sale)}</TableCell>

                      {/* Total */}
                      <TableCell className="text-right">
                        <div className="font-mono font-bold text-foreground">
                          {currencySymbol}
                          {total.toFixed(2)}
                        </div>
                        {balance > 0.01 && (
                          <div className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">
                            Due: {currencySymbol}
                            {balance.toFixed(2)}
                          </div>
                        )}
                      </TableCell>

                      {/* Single Action Button: "Select" */}
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          className="h-7 px-3 text-xs font-semibold cursor-pointer"
                          onClick={() => handleSelectSale(sale)}
                          disabled={selectingSaleId === sale.id}
                        >
                          {selectingSaleId === sale.id ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              Selecting...
                            </>
                          ) : (
                            "Select"
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </Modal>
  );
};

PosSalesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string,
  onLoadSaleToCart: PropTypes.func,
  mode: PropTypes.oneOf(["pos", "repair"]),
};

export default PosSalesModal;
